import { EnrichedRow } from "./derive";
import type { FacetOption } from "./Filters";

/** Display labels for the investmentPayment field (mirrors columns.tsx). */
const INVESTMENT_PAYMENT_LABEL: Record<string, string> = {
  CLIENTE: "Cliente",
  TI_MEDIA_GROUP_SL: "Ti Media Group SL",
};

/**
 * A column that can be filtered with a multi-select dropdown.
 * `getValue` returns the raw filter value ("" when the row has no value),
 * `formatOption` maps that raw value to the label shown in the dropdown.
 */
export interface FilterFieldDef {
  key: string;
  label: string;
  getValue: (r: EnrichedRow) => string;
  formatOption: (value: string) => string;
  minWidth: number;
  /** Width of the open dropdown when the options are longer than the field. */
  dropdownWidth?: number;
  /** Groups the options in the dropdown (e.g. briefings under their client). */
  groupBy?: (r: EnrichedRow) => string;
  /** Key of another field whose selection narrows these options. */
  narrowedBy?: string;
}

export const FILTER_FIELDS: FilterFieldDef[] = [
  {
    key: "companyName",
    label: "Cliente",
    getValue: (r) => r.companyName ?? "",
    formatOption: (v) => v,
    minWidth: 200,
  },
  {
    key: "briefingName",
    label: "Briefing",
    getValue: (r) => r.briefingName ?? "",
    formatOption: (v) => v,
    minWidth: 240,
    // Briefing names are long (09_2026_BUY_MEDIA_DIRECT_…): open wide enough
    // to read them whole instead of cutting them at the field's width.
    dropdownWidth: 640,
    groupBy: (r) => r.companyName || "Sin cliente",
    narrowedBy: "companyName",
  },
  {
    key: "platform",
    label: "Plataforma",
    getValue: (r) => r.platform ?? "",
    formatOption: (v) => v,
    minWidth: 170,
    dropdownWidth: 400,
  },
  {
    key: "briefingCampaignType",
    label: "Tipo Plataforma",
    getValue: (r) => r.briefingCampaignType ?? "",
    formatOption: (v) => v,
    minWidth: 170,
    dropdownWidth: 400,
  },
  {
    key: "sourceId",
    label: "Source ID",
    getValue: (r) => r.sourceId ?? "",
    formatOption: (v) => v,
    minWidth: 170,
    dropdownWidth: 400,
  },
  {
    key: "investmentPayment",
    label: "Quién paga",
    getValue: (r) => r.investmentPayment ?? "",
    formatOption: (v) => INVESTMENT_PAYMENT_LABEL[v] ?? v,
    minWidth: 170,
  },
];

/** Filters the WIP lists share: status, free text and per-field selections. */
export interface WipFilterState {
  statusFilter: string[];
  textFilter: string;
  fieldFilters: Record<string, string[]>;
}

/**
 * Distinct selectable values per filterable field (sorted, non-empty). A
 * field `narrowedBy` another only offers values from rows matching that
 * other field's selection (briefings of the chosen clients), and a grouped
 * field sorts by group first so the dropdown headings come out in order.
 */
export function buildFacets(
  rows: EnrichedRow[],
  fieldFilters: Record<string, string[]>
): Record<string, FacetOption[]> {
  const map: Record<string, FacetOption[]> = {};
  FILTER_FIELDS.forEach((f) => {
    const parent = f.narrowedBy
      ? FILTER_FIELDS.find((p) => p.key === f.narrowedBy)
      : undefined;
    const parentSelected = parent ? (fieldFilters[parent.key] ?? []) : [];
    const options = new Map<string, FacetOption>();
    rows.forEach((r) => {
      if (parent && parentSelected.length > 0) {
        if (!parentSelected.includes(parent.getValue(r))) return;
      }
      const v = f.getValue(r);
      if (v && !options.has(v)) {
        options.set(v, {
          value: v,
          label: f.formatOption(v),
          group: f.groupBy?.(r),
        });
      }
    });
    map[f.key] = Array.from(options.values()).sort(
      (a, b) =>
        (a.group ?? "").localeCompare(b.group ?? "") ||
        a.label.localeCompare(b.label)
    );
  });
  return map;
}

/** True when the row passes the status, field and text filters. */
export function matchesWipFilters(
  r: EnrichedRow,
  { statusFilter, textFilter, fieldFilters }: WipFilterState
): boolean {
  if (statusFilter.length > 0 && !statusFilter.includes(r.status)) return false;
  for (const f of FILTER_FIELDS) {
    const selected = fieldFilters[f.key];
    if (selected && selected.length > 0 && !selected.includes(f.getValue(r)))
      return false;
  }
  const text = textFilter.trim().toLowerCase();
  if (text) {
    const haystack = [
      r.companyName ?? "",
      r.briefingName ?? "",
      r.associatedCampaignName ?? "",
    ]
      .join(" ")
      .toLowerCase();
    if (!haystack.includes(text)) return false;
  }
  return true;
}
